import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useVertical } from '../../context/VerticalContext';
import { useRole } from '../../context/RoleContext';
import { Notice } from '../ui/Notice';

interface TaxBandStatus {
    taxable_profit: number;
    personal_allowance: number;
    band: 'personal_allowance' | 'starter' | 'basic' | 'intermediate' | 'higher' | 'advanced' | 'top' | 'additional';
    marginal_rate: number;
    next_band: string | null;
    next_band_at: number | null;
    headroom: number | null;
}

/**
 * Tells the user when their profit is about to cross — or has just crossed —
 * into a higher income tax band.
 *
 * The tax pot already reserves correctly through a band change; what was
 * missing was anyone being told. Crossing into higher rate means every further
 * pound of profit is taxed at 40% rather than 20%, which is exactly the sort of
 * thing worth knowing *before* year end, while there's still time to bring
 * forward a purchase or make a pension contribution.
 *
 * Band position comes from fn_get_tax_band_status so the thresholds live in SQL
 * beside fn_get_tax_pot, not in this file.
 *
 * Dismissal is per band: dismiss the "approaching higher rate" warning and it
 * stays gone until you actually cross, which raises a different message.
 */

/** Warn once profit is within this much of the next band. */
const WARN_WITHIN = 5000;

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
    }).format(amount);

export const TaxBandBanner = () => {
    const { activeOrgId } = useVertical();
    const { can } = useRole();
    const [status, setStatus] = useState<TaxBandStatus | null>(null);

    const storageKey = activeOrgId ? `fd_tax_band_dismissed_${activeOrgId}` : null;

    // Read straight from storage during render rather than syncing it in via an
    // effect — it's synchronous local state, not an external subscription.
    const [dismissedFor, setDismissedFor] = useState<string | null>(null);
    const storedDismissal = (() => {
        if (!storageKey) return null;
        try { return localStorage.getItem(storageKey); } catch { return null; }
    })();
    const dismissed = dismissedFor ?? storedDismissal;

    useEffect(() => {
        if (!activeOrgId || !can('dashboard')) return;
        let active = true;

        supabase.rpc('fn_get_tax_band_status').then(({ data, error }) => {
            if (!active || error || !data) return;
            setStatus(data as unknown as TaxBandStatus);
        });

        return () => { active = false; };
    }, [activeOrgId, can]);

    if (!status) return null;

    const crossedIntoHigher = ['higher', 'advanced', 'top', 'additional'].includes(status.band);
    const approaching =
        status.headroom !== null && status.next_band !== null && status.headroom <= WARN_WITHIN;

    if (!crossedIntoHigher && !approaching) return null;

    // Two distinct messages, dismissed independently — being warned you're close
    // and being told you've arrived are different pieces of news.
    const key = crossedIntoHigher ? `in_${status.band}` : `near_${status.band}`;
    if (dismissed === key) return null;

    const dismiss = () => {
        if (!storageKey) return;
        try { localStorage.setItem(storageKey, key); } catch { /* private mode */ }
        setDismissedFor(key);
    };

    const rate = `${Math.round(status.marginal_rate * 100)}%`;

    // Advisory, not an obligation — so it takes the quiet info tone, keeps the
    // figure and the band on screen, and puts the explanation behind Details.
    return (
        <Notice
            tone="info"
            icon={TrendingUp}
            title={crossedIntoHigher ? `You're in the ${rate} tax band` : 'Approaching the next tax band'}
            onDismiss={dismiss}
            dismissLabel="Dismiss tax band notice"
            details={
                <>
                    {crossedIntoHigher
                        ? <>Every further pound of profit this year is taxed at {rate}. Your tax pot already accounts for this.</>
                        : <>Past that point, further profit is taxed at the higher rate.</>}{' '}
                    <Link to="/help/tax-ni-vat-pots">What this means</Link>.
                </>
            }
        >
            {crossedIntoHigher ? (
                <>
                    Taxable profit <strong>{formatCurrency(status.taxable_profit)}</strong>. Further profit is taxed at{' '}
                    <strong>{rate}</strong>.
                </>
            ) : (
                <>
                    Taxable profit <strong>{formatCurrency(status.taxable_profit)}</strong> — about{' '}
                    <strong>{formatCurrency(status.headroom ?? 0)}</strong> below the {status.next_band}.
                </>
            )}
        </Notice>
    );
};
